import * as React from "react"
import {useState, useEffect} from "react"
import {Box, Chip, Alert, Typography, CircularProgress} from "@mui/material"
import {postSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"
import {createBatchFormData} from "../../@signals"

const extractId = (item) => item?.value ?? item
const extractIds = (items) => Array.isArray(items) ? items.map(i => i?.value ?? i).filter(x => x != null) : []

// Keep in sync with batch_creator.RAW2RAW_SPLIT_RECOMMEND_THRESHOLD
const SPLIT_RECOMMEND_THRESHOLD = 1000

export default function TaskEstimate() {
	const form = createBatchFormData.value
	const customerID = extractId(form.customerID)
	const brands = extractIds(form.brands)
	const sites = extractIds(form.sites)
	const tags = extractIds(form.tags)
	const topVentas = !!form.topVentas
	const splitBySites = form.splitByTargetSites !== false

	const [state, setState] = useState({loading: false, data: null, error: null})

	// Only the count inputs are in the debounce key; the split toggle only affects the
	// recommendation, which we recompute client-side (no extra round-trip).
	const key = JSON.stringify({customerID, brands, sites, tags, topVentas})

	useEffect(() => {
		const hasScope = brands.length > 0 || topVentas || tags.length > 0
		if (!customerID || !hasScope) {
			setState({loading: false, data: null, error: null})
			return
		}
		let cancelled = false
		setState(s => ({...s, loading: true, error: null}))
		const timer = setTimeout(async () => {
			try {
				const res = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/estimate`, {
					customerID, brands, sites, tags, topVentas, splitBySites
				})
				if (!cancelled) setState({loading: false, data: res, error: null})
			} catch (e) {
				if (!cancelled) setState({loading: false, data: null, error: 'Could not estimate task count'})
			}
		}, 450)
		return () => { cancelled = true; clearTimeout(timer) }
	}, [key])  // eslint-disable-line react-hooks/exhaustive-deps

	if (state.loading) {
		return (
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary'}}>
				<CircularProgress size={14} />
				<Typography variant="body2">Estimating tasks…</Typography>
			</Box>
		)
	}

	if (state.error) {
		return <Typography variant="body2" color="text.secondary">{state.error}</Typography>
	}

	const data = state.data
	if (!data) return null

	const total = data.total || 0
	const targetSites = data.targetSites || 0
	const batches = splitBySites ? targetSites : 1
	const recommend = !splitBySites && total > SPLIT_RECOMMEND_THRESHOLD
	const color = total > SPLIT_RECOMMEND_THRESHOLD ? 'warning' : (total > 0 ? 'success' : 'default')

	return (
		<Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
				<Chip color={color} variant="outlined" label={`≈ ${total.toLocaleString()} tasks`} />
				<Typography variant="body2" color="text.secondary">
					across {targetSites} competitor site{targetSites === 1 ? '' : 's'} · {batches} batch{batches === 1 ? '' : 'es'}
				</Typography>
			</Box>
			<Typography variant="caption" color="text.secondary">
				Estimate before dedup (already-queued and recently-matched products are skipped at run time).
			</Typography>
			{recommend && (
				<Alert severity="warning" sx={{py: 0}}>
					That's a lot for a single batch. Enable <strong>Split by site</strong> to create {targetSites} smaller
					batches (one per competitor site) instead of one large one.
				</Alert>
			)}
		</Box>
	)
}
